/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';

/**
 * @prop {String} label - Defines the label of the tool.
 * @prop {String} value - If the tool is of a select type, defines the label of it.
 * @prop {String} icon - Defines the icon that will be displayed for the tool. Required for every tool.
 * @prop {Boolean} active - Default false. If the tool is of a toggle type, defines if it is active (true) or not (false).
 * @prop {Boolean} icononly - Only displays icon and hides label.
 * @prop {Boolean} expanded - Default false. If the tool is of a select or palette type, defines if it's menu is opened (true) or not (false).
 * @prop {Boolean} disabled - Default false. Defines if a tool is disabled. If true, it won't allow clicks.
 * @prop {Boolean} toggle - Default false. If set to `true`, the tool will have a toggle behavior, turning on and off upon click/touch.
 * @prop {Boolean} select - Default false. If set to `true`, the tool will have a select behavior, opening a menu upon click/touch.
 * @prop {Boolean} palette - Default false. If set to `true`, the tool will have a palette button, with a button or toggle behavior on click and opening a menu upon clicking the menu icon.
 * @prop {Boolean} condensed -Default false. If set to `true`, the condensed tool style is applied.
 * @prop {Boolean} stretch - Default false. If set to `true`, the tool will occupy the entire width of it's container.
 * @prop {Boolean} flat - Default false.If set to true, the tool will have no background color applied to it.
 * @slot - Holds the array of items that will be shown as options in palette and select types. Takes Menu Items as children.
 */
export class SHTool extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      value: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      active: { type: Boolean, reflect: true },
      icononly: { type: Boolean, reflect: true },
      select: { type: Boolean, reflect: true },
      palette: { type: Boolean, reflect: true },
      toggle: { type: Boolean, reflect: true },
      expanded: { type: Boolean, reflect: true },
      disabled: { type: Boolean, reflect: true },
      condensed: { type: Boolean, reflect: true },
      stretch: { type: Boolean, reflect: true },
      flat: { type: Boolean, reflect: true },
    };
  }
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          padding: 0.1875rem;
          border: 0.0625rem solid transparent;
          background: rgba(var(--ui-1), var(--opacity-7));
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-radius: 0.125rem;
          cursor: pointer;
          transition: var(--transition-time) all ease-in-out;
          height: 4.25rem;
          min-height: 4.25rem;
          max-height: 4.25rem;
          flex: 1;
          position: relative;
          outline: 0rem;
        }

        :host([disabled]) {
          cursor: default;
          pointer-events: none;
        }
        :host(:not([active]):not([disabled]):not(.touch-device):hover),
        :host(:not([active]):not([disabled]):not(.touch-device):active) {
          background: rgba(var(--ui-1), var(--opacity-6));
        }
        :host([active]) {
          background: rgba(var(--ui-1), var(--opacity-5));
          border-color: rgba(var(--ui-1), var(--opacity-4));
        }
        :host(:not([stretch])) {
          width: 4.25rem;
          min-width: 4.25rem;
          max-width: 4.25rem;
          flex: 0;
        }
        :host([condensed]) {
          padding: 0.0625rem;
          height: 2.5rem;
          min-height: 2.5rem;
          max-height: 2.5rem;
        }
        :host([condensed]:not([stretch])) {
          min-width: 2.5rem;
          max-width: 2.5rem;
        }
        :host([select][value]) .tool-label,
        :host([palette][value]) .tool-label {
          font-size: 0.75rem;
          line-height: 1rem;
        }
        :host([icononly][icon]) .tool-label,
        :host(:not([label])) .tool-label,
        :host([condensed][icon]) .tool-label {
          display: none;
        }
        :host([flat]) {
          border-style: none;
          background: transparent;
          box-shadow: none;
        }
        :host([active][flat]) {
          background: rgba(var(--ui-1), var(--opacity-6));
          border: none;
        }
        :host(:not([active]):not([disabled]):not(.touch-device):hover[flat]),
        :host(:not([active]):not([disabled]):not(.touch-device):active[flat]) {
          background: rgba(var(--ui-1), var(--opacity-7));
        }
        .tool-label,
        .tool-value {
          height: auto;
          max-height: 2rem;
          text-align: center;
          color: var(--text-primary);
          line-height: 1rem;
          overflow: hidden;
          text-overflow: ellipsis;
          user-select: none;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          display: -webkit-box;
        }
        :host([icon]) .tool-label {
          height: 1.5rem;
          line-height: 0.75rem;
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }
        :host([disabled]) .tool-label,
        :host([disabled]) .tool-value {
          color: var(--text-disabled);
        }

        :host(:not([select][value])) .tool-value {
          display: none;
        }
        .palette {
          box-sizing: border-box;
          height: 1.25rem;
          width: 1.25rem;
          position: absolute;
          top: -0.0625rem;
          right: -0.0625rem;
        }
        .arrow {
          box-sizing: border-box;
          height: 0.375rem;
          width: 0.375rem;
          border-top: solid 0.188rem rgba(var(--ui-1), var(--opacity-4));
          border-right: solid 0.188rem rgba(var(--ui-1), var(--opacity-4));
          border-left: solid 0.188rem transparent;
          border-bottom: solid 0.188rem transparent;
          position: absolute;
          top: 0.1875rem;
          right: 0.1875rem;
        }
        :host([condensed]) .palette {
          box-sizing: border-box;
          height: 0.75rem;
          width: 0.75rem;
          position: absolute;
          top: -0.0625rem;
          right: -0.0625rem;
        }
        :host([condensed]) .arrow {
          box-sizing: border-box;
          height: 0.375rem;
          width: 0.375rem;
          border-top: solid 0.188rem rgba(var(--ui-1), var(--opacity-4));
          border-right: solid 0.188rem rgba(var(--ui-1), var(--opacity-4));
          border-left: solid 0.188rem transparent;
          border-bottom: solid 0.188rem transparent;
          position: absolute;
          top: 0.1875rem;
          right: 0.1875rem;
        }

        :host(:not([palette]):not([select])) sh-popover {
          transition: none;
        }
        :host([disabled]) .arrow {
          opacity: 0.2;
        }
        :host([readonly]) .arrow {
          opacity: 0;
        }
        sh-popover:not([mobile]) {
          position: fixed;
          background: rgba(var(--ui-8), var(--opacity-1));
          z-index: 999999;
          max-height: 12.5rem;
          overflow: auto;
        }
      `,
    ];
  }

  render() {
    return html`
      ${this.icon || this.icononly
        ? html`<sh-icon
            icon="${this.icon}"
            class="tool-icon"
            ?disabled="${this.disabled}"
          ></sh-icon>`
        : html``}
      ${this.label && !this.icononly
        ? html`<sh-text size="body-1" class="tool-label">${this.label}</sh-text>`
        : html``}
      ${this.value || !this.icononly || !this.icon
        ? html`<sh-text size="body-1" class="tool-value">${this.value}</sh-text>`
        : html``}
      ${this.palette || this.select ? html`<div class="arrow"></div>` : html``}
      <div class="palette" @click="${(e) => this._expandPalette(e)}"></div>
      ${this.palette || this.select
        ? html`
            <sh-popover id="menu" ?visible="${this.expanded}">
              <slot></slot>
            </sh-popover>
          `
        : html``}
    `;
  }
  constructor() {
    super();
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('aria-labelledby') && this.label) {
      this.setAttribute('aria-labelledby', this.label);
    }
    this.addEventListener('click', function (e) {
      if (e.target.tagName === 'SH-TOOL') {
        if (this.toggle && e.composedPath()[0].className !== 'palette') {
          this.active = !this.active;
        }
        if (this.select) {
          this.expanded = true;
          this._setActiveState();
          this._handleOffset();
        }
      }
    });
    this._setInitialValuesOnLoad();
    /**@ignore */
    this.documentClick = this._closeTool.bind(this);
    document.body.addEventListener('click', this.documentClick, true);
    document.body.addEventListener('touchstart', this.documentClick, true);
  }
  firstUpdated() {
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
    // listen to dynamic menu-item change
    this.addEventListener('menu-item-active', this._activeChanged);
    // listen to menu-item clicked event
    const menu = this.shadowRoot.querySelector('#menu');
    if (menu) {
      this.shadowRoot.querySelector('#menu').addEventListener('clicked', this._activeChanged);
    }
    // remove context menu default behavior
    this.addEventListener('contextmenu', function (e) {
      e.preventDefault();
    });
    // long touch palette listener
    this.addEventListener('touchstart', function () {
      if (this.palette) {
        this._delay = setTimeout(() => {
          this._expandPalette();
        }, 500);
      }
    });
    this.addEventListener('touchend', function () {
      clearTimeout(this._delay);
    });
  }
  updated(changedProperties) {
    if (changedProperties.has('expanded')) {
      this._clickListener();
    }
    if (changedProperties.has('disabled')) {
      this.expanded = false;
    }
    const listOfProperties = ['label', 'value', 'active', 'icon', 'expanded', 'disabled', 'flat'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  disconnectedCallback() {
    document.body.removeEventListener('click', this.documentClick);
    document.body.removeEventListener('touchstart', this.documentClick);
    window.cancelAnimationFrame(this._reposition);
    if (this._reposition) {
      clearInterval(this._reposition);
    }
    super.disconnectedCallback();
  }
  _closeTool(e) {
    if (e.target !== this && !e.touches) {
      this.expanded = false;
    }
    if (e.touches && e.composedPath().indexOf(this) === -1) {
      this.expanded = false;
    }
  }
  _clickListener() {
    if (this._reposition) {
      window.cancelAnimationFrame(this._reposition);
    }
    this._reposition = window.requestAnimationFrame(this._handleOffset.bind(this));
    if (!this.expanded) {
      window.cancelAnimationFrame(this._reposition);
    }
  }
  _handleOffset() {
    let menu;
    const baseFontSize = parseFloat(
      window.getComputedStyle(document.querySelector('html'), null).getPropertyValue('font-size')
    );
    menu = this.shadowRoot.querySelector('#menu');
    menu.visible = this.expanded;
    this._reposition = window.requestAnimationFrame(this._handleOffset.bind(this));
    // define tool offset
    const offsetY = this.getBoundingClientRect().top;
    const offsetX = this.getBoundingClientRect().left;
    // position menu
    const menuHeight = menu.clientHeight;
    const menuWidth = menu.clientWidth;
    // position menu in Y axis
    if (offsetY + menuHeight > window.innerHeight) {
      menu.style.top = `${(offsetY - menuHeight + this.offsetHeight) / baseFontSize}rem`;
    } else {
      menu.style.top = `${offsetY / baseFontSize}rem`;
    }
    // position menu in X axis
    if (offsetX + this.offsetWidth + menuWidth > window.innerWidth) {
      menu.style.left = `${(offsetX - menuWidth) / baseFontSize}rem`;
    } else {
      menu.style.left = `${(offsetX + this.offsetWidth) / baseFontSize}rem`;
    }
  }
  _expandPalette(e) {
    if (e) {
      e.stopPropagation();
    }
    this._setActiveState();
    this.expanded = true;
    this._handleOffset();
  }
  _setActiveState() {
    for (let i = 0; i < this.children.length; i++) {
      if (this.label === this.children[i].label || this.value === this.children[i].label) {
        this.children[i].active = true;
        break;
      }
    }
  }
  _setInitialValuesOnLoad() {
    for (let i = 0; i < this.childElementCount; i++) {
      if (this.children[i].active) {
        this._setMenuItemLabelIcon(this.children[i]);
      }
    }
  }
  _setMenuItemLabelIcon(menuItem) {
    if (!this.select) {
      this.icon = menuItem.icon;
      if (this.palette) {
        this.label = menuItem.label;
      }
    } else {
      /**
       * If tool is of type 'select' and
       * if the clicked menu item does not have icon,
       * then the tool's value gets updated with the label of the menu-item.
       * hide the icon and show only value and label.
       *
       * if the clicked menu-item has icon,
       * then the tool's label gets updated with the label of the menu-item.
       * hide the value and show only icon and label.
       */
      if (!menuItem.icon) {
        this.value = menuItem.label;
      } else {
        this.icon = menuItem.icon;
        this.label = menuItem.label;
      }
    }
  }
  _activeChanged(e) {
    const tool = e.target.parentElement;
    if (e.type === 'clicked' && e.target.tagName === 'SH-MENU-ITEM') {
      e.stopPropagation();
      tool.expanded = false;
    }
    for (let i = 0; i < this.children.length; i++) {
      if (this.label === this.children[i].label) {
        this.children[i].active = true;
        break;
      }
    }
    tool._unselectSiblings(e.target.label);
    tool._setMenuItemLabelIcon(e.target);
  }
  _unselectSiblings(label) {
    for (let i = 0; i < this.children.length; i++) {
      if (label !== this.children[i].label) {
        this.children[i].removeAttribute('active');
      }
    }
  }
}
customElements.define('sh-tool', SHTool);
